import type {
  CBFViolation,
  ListViolatedCBFsResponse,
} from '@sb/messenger/auto_generated/messenger/compliance_control_bot_service';
import type { ComplianceControlBotConnector } from '@sb/messenger/connectors/generated';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';

import type { CBFAxis, CBFSign } from './cbfLabels';
import {
  EXTERNAL_COLLISION_LABEL,
  FORCE_LIMIT_LABEL,
  SELF_COLLISION_LABEL,
  SINGULARITY_LABEL,
  eeBoundaryLabel,
  jointLimitLabel,
  wholeBodyBoundaryLabel,
} from './cbfLabels';

/**
 * Maps a raw CBF barrier name (as produced by the compliance controller's
 * `_generate_barrier_names`, optionally `stageN:`-prefixed) to the operator-
 * facing label. Uses the shared label constructors in `./cbfLabels` so the
 * wording stays in lockstep with the in-session teleop toasts. Falls back to
 * the de-prefixed raw name for anything unrecognized.
 */
export function labelForBarrier(rawName: string): string {
  const name = rawName.replace(/^stage\d+:/, '');

  const ee = name.match(/^ee_set_containment_(max|min)_([xyz])$/);

  if (ee) {
    const sign: CBFSign = ee[1] === 'max' ? '+' : '−';

    return eeBoundaryLabel(sign, ee[2].toUpperCase() as CBFAxis);
  }

  const joint = name.match(/^joint_limit_(max|min)_(\d+)$/);

  if (joint) {
    return jointLimitLabel(
      Number(joint[2]),
      joint[1] === 'max' ? 'upper' : 'lower',
    );
  }

  if (name === 'singularity_avoidance') return SINGULARITY_LABEL;

  const wb = name.match(/^whole_body_(upper|lower)_([xyz])_\d+$/);

  if (wb) {
    const sign: CBFSign = wb[1] === 'upper' ? '+' : '−';

    return wholeBodyBoundaryLabel(sign, wb[2].toUpperCase() as CBFAxis);
  }

  if (name.startsWith('collision_avoidance_')) return EXTERNAL_COLLISION_LABEL;
  if (name.startsWith('self_collision_')) return SELF_COLLISION_LABEL;
  if (name.startsWith('force_limit_')) return FORCE_LIMIT_LABEL;

  return name;
}

/** De-duplicated, operator-facing labels for a set of violated CBFs. */
export function formatViolations(violations: CBFViolation[]): string {
  const labels: string[] = [];

  violations.forEach((v) => {
    const label = labelForBarrier(v.name);

    if (!labels.includes(label)) labels.push(label);
  });

  return labels.join(', ');
}

export type CBFCheckResult =
  | { ok: true }
  | { ok: false; reason: string; violations: CBFViolation[] };

/** Operator-facing reason used whenever the controller can't be evaluated. */
const CANNOT_VERIFY_REASON =
  'compliance controller could not verify the robot is within bounds';

/** Operator-facing reason when the compliance connector isn't wired up. */
const NOT_CONNECTED_REASON = 'compliance control bot not connected';

// Generous ceiling for the one-shot query: the controller's first h(x) call may
// trace/compile on control-box CPU. Well above expected latency, but bounded so
// a wedged/absent controller fails closed promptly rather than inheriting the
// messenger default (5s) and, worse, escaping as an unhandled rejection (which
// would surface as a hidden Internal routine failure).
const LIST_VIOLATED_CBFS_TIMEOUT_MS = 15_000;

/**
 * The single teleop-start compliance gate, shared by every entry point (the
 * raw `/teleop/start` and `startTeleopSession` controllers in
 * `libs/sb-robot-api`, and the Teleop routine step). Owns the whole policy so
 * call sites collapse to one branch:
 *   - `complianceMode` flag off        -> `{ ok: true }` (nothing to check)
 *   - connector missing                -> not-connected reason (fail closed)
 *   - RPC error/timeout or unavailable -> cannot-verify reason (fail closed)
 *   - any enabled CBF violated         -> "in violation of <labels>"
 *   - otherwise                        -> `{ ok: true }`
 *
 * The `reason` is phrased to append after "Cannot start teleop: ".
 */
export async function checkEnabledCBFViolations(
  connector: ComplianceControlBotConnector | undefined,
): Promise<CBFCheckResult> {
  if (!getFeatureFlagSync('complianceMode', false)) {
    return { ok: true };
  }

  if (!connector) {
    return { ok: false, reason: NOT_CONNECTED_REASON, violations: [] };
  }

  let result: ListViolatedCBFsResponse;

  try {
    result = await connector.ListViolatedCBFs(
      {},
      LIST_VIOLATED_CBFS_TIMEOUT_MS,
    );
  } catch {
    // RPC timeout / controller down / transport error — fail closed with an
    // operator-facing reason rather than letting the rejection escape (which
    // the raw start path turns into a REQ-TIMEOUT blob and the routine step
    // into a reason-hiding Internal failure).
    return { ok: false, reason: CANNOT_VERIFY_REASON, violations: [] };
  }

  if (!result.available) {
    return { ok: false, reason: CANNOT_VERIFY_REASON, violations: [] };
  }

  if (result.violations.length > 0) {
    return {
      ok: false,
      reason: `in violation of ${formatViolations(result.violations)}`,
      violations: result.violations,
    };
  }

  return { ok: true };
}
